import * as React from 'react'
import {ChangeEvent, Component, ReactElement, RefObject} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {PET_TYPE} from '../../../../../CodeMaster'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {
    ApplyButton,
    ItemHeading,
    ListGridContainer,
    WideWhiteArea,
} from '../../common/resident/NatResidentApplicationsCommon'
import {NatResidentPetMedicalCheckUpReportLocationState} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import PPRadioGroupForResident from '../../../../../components/shared/forms/resident/PPRadioGroupForResident'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import styled from 'styled-components'
import {DisplayItemInputTitleRequiredNoMargin} from '../../../../../styledComponents/DisplayItem'
import {NatPutPetMedicalCheckUpReportApplyRequest} from '../../repository/NatPutPetMedicalCheckUpReport'
import ResidentAttachmentFileArea, {
    FileInformation,
} from '../../../../../components/applications/ResidentAttachmentFileArea'
import {Colors} from '../../../../../styledComponents/Constants'
import {StyledSecondaryButton} from '../../../../../styledComponents/Button'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    navigate: NavigateFunction
    location: Location<NatResidentPetMedicalCheckUpReportLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    petType: Nullable<string>
    petBreed: Nullable<string>
    dogLicenseFile: Nullable<FileInformation>
    rabiesFile: Nullable<FileInformation>
    medicalCheckUpReport: Nullable<FileInformation[]>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutPetMedicalCheckUpReportApplyRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    petType: string
    petBreed: string
    dogLicenseFile: Nullable<FileInformation>
    rabiesFile: Nullable<FileInformation>
    medicalCheckUpReport: FileInformation[]

    clearHavePetStartError: boolean
    applyButtonDisabled: boolean
    applyToAttachmentAddDisplay: boolean
    applyToAttachmentNoSelect: boolean
    isLoadCompleted: boolean
}

export default class NatResidentPetMedicalCheckUpReportForm extends Component<Props, State> {
    attachmentRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applyToAttachmentAddDisplay: true,
            applyToAttachmentNoSelect: false,
            applicantFamilyName: '',
            applicantFirstName: '',
            petType: '',
            petBreed: '',
            dogLicenseFile: null,
            rabiesFile: null,
            medicalCheckUpReport: [],
            clearHavePetStartError: false,
            isLoadCompleted: false
        }

        this.attachmentRef = React.createRef()

        this.handleChangePetType = this.handleChangePetType.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.handleDogLicenseFileInput = this.handleDogLicenseFileInput.bind(this)
        this.handleRabiesFileInput = this.handleRabiesFileInput.bind(this)
        this.handleMedicalCheckUpReportInput = this.handleMedicalCheckUpReportInput.bind(this)
        this.handleDogLicenseFileDelete = this.handleDogLicenseFileDelete.bind(this)
        this.handleRabiesFileDelete = this.handleRabiesFileDelete.bind(this)
        this.handleApplyToAttachmentDelete = this.handleApplyToAttachmentDelete.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const petType = this.props.petType ?? this.state.petType
            const petBreed = this.props.petBreed ?? this.state.petBreed
            const dogLicenseFile = this.props.dogLicenseFile ?? this.state.dogLicenseFile
            const rabiesFile = this.props.rabiesFile ?? this.state.rabiesFile
            const medicalCheckUpReport = this.props.medicalCheckUpReport ?? this.state.medicalCheckUpReport
            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                petType: petType,
                petBreed: petBreed,
                dogLicenseFile: dogLicenseFile,
                rabiesFile: rabiesFile,
                medicalCheckUpReport: medicalCheckUpReport
            }, () => {
                this.updateButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateButtonDisabled()
        })
    }

    handleDogLicenseFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const fileName = event.target.files[0].name
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                const fileData: string[] = String(reader.result).split(',')
                if (fileData[1]) {
                    this.setState({
                        dogLicenseFile: {
                            fileName: fileName,
                            fileData: fileData[1],
                            fileNumber: null,
                            deleteFlag: false,
                        },
                    }, () => {
                        this.updateButtonDisabled()
                    })
                }
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.attachmentRef.current && this.attachmentRef.current.value) {
            this.attachmentRef.current.value = ''
        }
    }

    handleRabiesFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const fileName = event.target.files[0].name
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                const fileData: string[] = String(reader.result).split(',')
                if (fileData[1]) {
                    this.setState({
                        rabiesFile: {
                            fileName: fileName,
                            fileData: fileData[1],
                            fileNumber: null,
                            deleteFlag: false,
                        },
                    }, () => {
                        this.updateButtonDisabled()
                    })
                }
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.attachmentRef.current && this.attachmentRef.current.value) {
            this.attachmentRef.current.value = ''
        }
    }


    handleMedicalCheckUpReportInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {medicalCheckUpReport} = this.state
            const fileName = event.target.files[0].name
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                const fileData: string[] = String(reader.result).split(',')
                if (fileData[1]) {
                    medicalCheckUpReport.push({
                        fileName: fileName,
                        fileData: fileData[1],
                        fileIndex: medicalCheckUpReport.length,
                        fileNumber: null,
                        deleteFlag: false,
                    })
                    this.setState({
                        medicalCheckUpReport: medicalCheckUpReport,
                    }, () => {
                        this.updateButtonDisabled()
                    })
                }
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.attachmentRef.current && this.attachmentRef.current.value) {
            this.attachmentRef.current.value = ''
        }
    }

    handleDogLicenseFileDelete(): void {
        this.setState({
            dogLicenseFile: null,
        }, () => {
            this.updateButtonDisabled()
        })
    }

    handleRabiesFileDelete(): void {
        this.setState({
            rabiesFile: null,
        }, () => {
            this.updateButtonDisabled()
        })
    }


    handleApplyToAttachmentDelete(file: FileInformation): void {
        const fileIndex = file.fileIndex ?? 0
        const applyToAttachmentList = this.state.medicalCheckUpReport
        applyToAttachmentList[fileIndex] = file

        this.setState({
            medicalCheckUpReport: applyToAttachmentList,
        }, () => {
            this.updateButtonDisabled()
        })
    }

    handleChangePetType(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            petType: event.target.value,
            dogLicenseFile: null,
            rabiesFile: null
        }, () => {
            this.updateButtonDisabled()
        })
    }

    handleClickAddFileButton(elementId: string): void {
        const fileUpload = document.getElementById(elementId) as HTMLInputElement
        fileUpload.value = ''
        if (fileUpload) {
            fileUpload.click()
        }
    }

    updateButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, petType, petBreed,
            dogLicenseFile, rabiesFile, medicalCheckUpReport
        } = this.state

        let applyButtonDisabled = false
        const inputApplyToAttachment = medicalCheckUpReport.filter(file => !file.deleteFlag)

        if (!applicantFamilyName || !applicantFirstName || !petType ||
            !petBreed || inputApplyToAttachment.length === 0) {
            applyButtonDisabled = true
        }

        if (petType === PET_TYPE.DOG.value) {
            if (dogLicenseFile && rabiesFile) {
                if ((!dogLicenseFile.fileName || !dogLicenseFile.fileData) && !dogLicenseFile.fileNumber) {
                    applyButtonDisabled = true
                }
                if ((!rabiesFile.fileName || !rabiesFile.fileData) && !rabiesFile.fileNumber) {
                    applyButtonDisabled = true
                }
            } else {
                applyButtonDisabled = true
            }
        }
        this.setState({
            applyButtonDisabled: applyButtonDisabled,
            applyToAttachmentAddDisplay: !(inputApplyToAttachment.length >= 10),
            applyToAttachmentNoSelect: (inputApplyToAttachment.length === 0),
        })
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, petType,
            petBreed, dogLicenseFile, rabiesFile, medicalCheckUpReport
        } = this.state

        this.setState({clearHavePetStartError: false})

        const inputApplyToAttachment = medicalCheckUpReport.filter(attachmentFile => !attachmentFile.deleteFlag)

        const request: NatPutPetMedicalCheckUpReportApplyRequest = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            petType: petType,
            petBreed: petBreed,
            healthCenterDogLicense: dogLicenseFile,
            rabiesPreventionInjectionCertification: rabiesFile,
            medicalCheckUpReport: inputApplyToAttachment,
            confirmed: false
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        onChange: this.handleChangeText,
                                        placeholder: '例：素敵'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        value: applicantFirstName,
                                        placeholder: '例：太郎'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPetTypeForm(): ReactElement {
        const {petType} = this.state

        return (
            <>
                <ItemHeading>
                    種類
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        飼育しているペットの種類
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'20px'}/>
                    <GridContainer>
                        <GridItem over={12}>
                            <PPRadioGroupForResident
                                data={PET_TYPE.values()}
                                selectValue={petType}
                                onChange={this.handleChangePetType}
                                eventKeys={['petType']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        </GridItem>
                    </GridContainer>
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPetBreedForm(): ReactElement {
        const {petBreed} = this.state

        return (
            <>
                <ItemHeading>
                    品種
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>飼育している<BreakPcOnly/>ペットの品種</span>}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        maxLength: 50,
                                        name: 'petBreed',
                                        value: petBreed,
                                        placeholder: '例：チワワ'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['petBreed']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPetTypeForDogInputForm(): ReactElement {
        const {dogLicenseFile, rabiesFile, petType} = this.state

        if (petType !== PET_TYPE.DOG.value) {
            return (<></>)
        }
        return (
            <>
                <ItemHeading>
                    保健所犬鑑札
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        お住まいの市区町村へ飼い犬を登録した際に交付された「犬鑑札」を提出
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'5px'}/>
                    <FileRemarkContainer fontSize={'12px'}>
                        <div>・</div>
                        <div>
                            1ファイルまで添付可能です。
                        </div>
                        <div>・</div>
                        <div>
                            ファイルは画像またはPDFでご提出ください。
                        </div>
                    </FileRemarkContainer>
                    <Box height={'10px'}/>
                    {!dogLicenseFile &&
                        <StyledSecondaryButton
                            onClick={() => this.handleClickAddFileButton('fileUpload-DogLicenseFile')}>
                            {'ファイルを選択する'}
                        </StyledSecondaryButton>
                    }
                    <DisplayNoneFileInput
                        type={'file'}
                        multiple
                        id={'fileUpload-DogLicenseFile'}
                        ref={this.attachmentRef}
                        onChange={this.handleDogLicenseFileInput}
                    />
                    <Box height={'30px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'選択中の添付資料'}
                        component={
                            dogLicenseFile ?
                                <ResidentAttachmentFileArea
                                    fileInformation={dogLicenseFile}
                                    index={0}
                                    onDelete={this.handleDogLicenseFileDelete}
                                    eventKeys={[`healthCenterDogLicense`]}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                : <StyledNoFile>選択されていません</StyledNoFile>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
                <Box height={'15px'}/>
                <ItemHeading>
                    狂犬病予防注射済証
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        狂犬病予防注射の接種を受けた際に交付された「狂犬病予防注射済証」を提出
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'5px'}/>
                    <FileRemarkContainer fontSize={'12px'}>
                        <div>・</div>
                        <div>
                            1ファイルまで添付可能です。
                        </div>
                        <div>・</div>
                        <div>
                            ファイルは画像またはPDFでご提出ください。
                        </div>
                    </FileRemarkContainer>
                    <Box height={'10px'}/>
                    {!rabiesFile &&
                        <StyledSecondaryButton
                            onClick={() => this.handleClickAddFileButton('fileUpload-rabiesFile')}>
                            {'ファイルを選択する'}
                        </StyledSecondaryButton>
                    }
                    <DisplayNoneFileInput
                        type={'file'}
                        multiple
                        id={'fileUpload-rabiesFile'}
                        ref={this.attachmentRef}
                        onChange={this.handleRabiesFileInput}
                    />
                    <Box height={'30px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'選択中の添付資料'}
                        component={
                            rabiesFile ?
                                <ResidentAttachmentFileArea
                                    fileInformation={rabiesFile}
                                    index={0}
                                    onDelete={this.handleRabiesFileDelete}
                                    eventKeys={[`rabiesPreventionInjectionCertification`]}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                : <StyledNoFile>選択されていません</StyledNoFile>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
                <Box height={'15px'}/>
            </>)
    }

    renderAttachmentImageInputForm(): ReactElement {
        const {medicalCheckUpReport, applyToAttachmentAddDisplay, applyToAttachmentNoSelect} = this.state

        return (
            <>
                <ItemHeading>
                    健康診断書
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        健康診断を受けた際に発行された「健康診断書」を提出
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'5px'}/>
                    <FileRemarkContainer fontSize={'12px'}>
                        <div>・</div>
                        <div>
                            10ファイルまで添付可能です。
                        </div>
                        <div>・</div>
                        <div>
                            ファイルは画像またはPDFでご提出ください。
                        </div>
                    </FileRemarkContainer>
                    <Box height={'10px'}/>
                    {applyToAttachmentAddDisplay &&
                        <StyledSecondaryButton
                            onClick={() => this.handleClickAddFileButton('fileUpload-attachmentFile')}>
                            {applyToAttachmentNoSelect ?
                                'ファイルを選択する' : 'ファイルを追加する'
                            }
                        </StyledSecondaryButton>
                    }
                    <DisplayNoneFileInput
                        type={'file'}
                        multiple
                        id={'fileUpload-attachmentFile'}
                        ref={this.attachmentRef}
                        onChange={this.handleMedicalCheckUpReportInput}
                    />
                    <Box height={'30px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'選択中の添付資料'}
                        component={
                            !applyToAttachmentNoSelect ?
                                medicalCheckUpReport.map((attachmentFile, index) => (
                                    !medicalCheckUpReport[index].deleteFlag &&
                                    <ResidentAttachmentFileArea
                                        key={index}
                                        fileInformation={medicalCheckUpReport[index]}
                                        index={index}
                                        onDelete={this.handleApplyToAttachmentDelete}
                                        eventKeys={[`medicalCheckUpReport[${index}]`]}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                )) : <StyledNoFile>選択されていません</StyledNoFile>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>)
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderPetTypeForm()}
                <Box height={'15px'}/>
                {this.renderPetBreedForm()}
                <Box height={'15px'}/>
                {this.renderPetTypeForDogInputForm()}
                {this.renderAttachmentImageInputForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
const FileRemarkContainer = styled(ListGridContainer)`
  color: ${Colors.AccentDark};
`
const StyledNoFile = styled.div`
  color: ${Colors.AccentLight};
  ${currentMedia.lessThan('sp')`
    margin-left: 18px;
  `};
`
const DisplayNoneFileInput = styled.input`
  display: none;
`
